import React, { FC, useState, useEffect, useRef } from 'react'
import { observer } from 'mobx-react'
import { RouteComponentProps } from 'react-router-dom'
import { formatCardNumber, useHistoryNew, toast, checkExpire, modalLayer } from '@utils'
import { IonPage, IonContent } from '@ionic/react'
import { UseHeader, MktButton, StoreShow, SendInfoModal, ContactType } from '@cps'
import { memberMobx } from '@mobxStore'
import { storeMobx } from 'restaurant'
import { format, ddayjs, getHandTopUrl } from '@dt/fe-utils'
import mktController from 'controllers/mktController'
import { map, find } from '@dt/fe-utils/dist/lodash'
import QRCode from 'qrcode.react'
import i18n from 'i18next'
import JsBarcode from 'jsbarcode'
import './index.scss'

interface IProps extends IPage, RouteComponentProps { }

const GiftCardDetail: FC<IProps> = ({ match }) => {
  const history = useHistoryNew()
  const { storeList } = storeMobx
  const { cardNumber } = match.params as any
  const { giftCardList } = memberMobx
  const [giftCardInfo, setGiftCardInfo] = useState(null)
  const [storeArr, setStoreArr] = useState<any[]>([])
  const barcodeRef = useRef<any>(null)

  const goRecharge = () => {
    if (giftCardInfo.status !== 'enable') {
      toast({ message: i18n.t('mkt.giftCard.cardIsFrozen') })
      return
    }
    const url: string = `/iGc/recharge/${giftCardInfo.memberCardId}`
    history.push(url)
  }

  const handleStoreName = (storeIds) => {
    const list = map(storeIds, (i: string) => {
      const findItem = find(storeList, (item: any) => item.storeId === i)
      return findItem ? findItem.storeName : ''
    })
    return list
  }

  const goRecord = (type: 'consume' | 'integral') => {
    history.push(`/iRecord/flow/${type}/${giftCardInfo.cardNo}`)
  }

  const giveCard = () => {
    modalLayer(ContactType, { title: i18n.t('mkt.giftCard.giveMethod'), hasThird: true }).then((type: any) => {
      if (['email', 'mobile'].includes(type)) {
        modalLayer(SendInfoModal, { type: type }, 'sendInfoModal').then((val: any) => {
          const para: any = {
            cardNo: giftCardInfo.cardNo,
            giverName: val.givePerson,
            receiverName: val.receivedPerson,
            message: val.message,
          }
          if (type === 'email') {
            para.receiverEmail = val.user
          } else {
            para.receiverMobile = val.user
            para.countryCode = val.countryCode
          }
          memberMobx.giveCard(para).then(() => {
            toast({ message: i18n.t('mkt.giftCard.giveSuccess'), position: 'bottom' })
            mktController.goBack(history)
          })
        })
      } else {
        const para: any = {
          cardNo: giftCardInfo.cardNo,
          giverName: memberMobx.memberInfo.nickName || '',
          receiverName: '',
          message: i18n.t('mkt.giftCard.sendMessage')
        }
        memberMobx.giveCard(para).then((res: any) => {
          if (res) {
            const url: string = `${getHandTopUrl()}/mmg/#/giftCard/give/${res.cardNo}/${res.giftcardGiveId}?ct=brand&b=${storeMobx.brand.brandId}`
            mktController.jumpMmgUrl(url, i18n.t('mkt.member.sendGcText', { name: para.giverName }), giftCardInfo.cover)
          }
        })
      }
    })
  }

  useEffect(() => {
    if (giftCardInfo && barcodeRef.current) {
      JsBarcode(barcodeRef.current, `${giftCardInfo.cardNo}`, {
        displayValue: false,
        width: 2,
        height: 44,
        margin: 8,
      })
    }
  }, [giftCardInfo, barcodeRef])

  useEffect(() => {
    if (giftCardList.length > 0) {
      const cardInfo = giftCardList.find((i: any) => i.cardNo === cardNumber)
      if (cardInfo) {
        setGiftCardInfo(cardInfo)
      } else {
        mktController.goBack(history)
      }
    } else {
      mktController.goBack(history)
    }
  }, [cardNumber, giftCardList])

  useEffect(() => {
    if (giftCardInfo) {
      if (giftCardInfo.giftcard.isAllStore) {
        const storeArr: any[] = []
        map(storeList, (item: any) => {
          item.storeId && storeArr.push(item.storeId)
        })
        setStoreArr(storeArr)
      } else {
        setStoreArr(giftCardInfo.giftcard.storeIds)
      }
    }
  }, [giftCardInfo])

  return (
    <IonPage className='my-gc-detail-page'>
      <UseHeader onBack={() => mktController.goBack(history)} title={i18n.t('pageName.giftCard')}></UseHeader>
      <IonContent className='page-content'>
        {
          giftCardInfo &&
            <div className='modal-body'>
              <div className='detail-group detail-card'>
                <div className='card-cover' style={{ backgroundImage: `url(${giftCardInfo.cover})` }}></div>
                <div className='card-name'>{giftCardInfo.giftcard.giftcardName}</div>
                <div className='group-line'></div>
                <div className='card-info'>
                  <div className='info-area'>
                    <div className='balance'>{format.toPrice(giftCardInfo.balance)}</div>
                    <div className='cardno'>{formatCardNumber(giftCardInfo.cardNo)}</div>
                    <div>Pin code: {giftCardInfo.cardPin}</div>
                  </div>
                  <div className='btn-area'>
                    {giftCardInfo.giftcard.allowAgainLoad && (giftCardInfo.expireTime === null || !checkExpire(giftCardInfo.expireTime, ddayjs().format('YYYY-MM-DD HH:mm:ss'))) && <MktButton size='small' text={i18n.t('mkt.member.rechargeBalance')} onClick={goRecharge}></MktButton>}
                  </div>
                </div>
                <div className='group-line'></div>
                <div className='er-area'>
                  <QRCode value={giftCardInfo.cardNo} size={200} level='H' />
                  <div className='bar-area'>
                    <svg className='bar-code' ref={barcodeRef} />
                  </div>
                </div>
                <div className='group-line'></div>
                <div className='opera-area'>
                  <div data-cypid='mmgToGCardRechargeFlow' className='opera-item' onClick={() => goRecord('consume')}>
                    <div className='icon'><i className='iconfont icon-chongzhiliushui'></i></div>
                    <div>{i18n.t('mkt.member.rechargeFlow')}</div>
                  </div>
                  {
                    giftCardInfo.balance > 0 && (giftCardInfo.expireTime === null || !checkExpire(giftCardInfo.expireTime, ddayjs().format('YYYY-MM-DD HH:mm:ss'))) &&
                      <>
                        <div className='center-line'></div>
                        <div data-cypid='mmgToGiveGCard' className='opera-item' onClick={() => giveCard()}>
                          <div className='icon'><i className='iconfont icon-kazhuanzeng'></i></div>
                          <div>{i18n.t('mkt.giftCard.cardTransfer')}</div>
                        </div>
                      </>
                  }
                </div>
              </div>
              <div className='detail-group'>
                <div className='group-top'>
                  <div className='title'>{i18n.t('mkt.giftCard.basicAttr')}</div>
                </div>
                <div className='basic-list'>
                  <div className='basic-item'>
                    <span>{i18n.t('mkt.giftCard.cardClass')}</span>
                    <span className='info'>{giftCardInfo.giftcard.giftcardName}</span>
                  </div>
                  <div className='basic-item'>
                    <span>{i18n.t('mkt.giftCard.cardBalance')}</span>
                    <span className='info'>{format.toPrice(giftCardInfo.balance)}</span>
                  </div>
                  <div className='basic-item'>
                    <span>{i18n.t('mkt.giftCard.cardNumber')}</span>
                    <span className='info'>{formatCardNumber(giftCardInfo.cardNo)}</span>
                  </div>
                  <div className='basic-item'>
                    <span>{i18n.t('mkt.giftCard.currentState')}</span>
                    <span className='info'>{giftCardInfo.status === 'enable' ? i18n.t('mkt.giftCard.activated') : i18n.t('mkt.giftCard.frozen')}</span>
                  </div>
                  <div className='basic-item'>
                    <span>{i18n.t('mkt.giftCard.typesOf')}</span>
                    <span className='info'>{giftCardInfo.physicalcardId ? i18n.t('mkt.giftCard.plastic') : i18n.t('mkt.giftCard.eleCard')}</span>
                  </div>
                  <div className='basic-item'>
                    <span>{i18n.t('mkt.giftCard.validPeriod')}</span>
                    <span className='info'>{giftCardInfo.expireTime ? giftCardInfo.expireTime : i18n.t('mkt.giftCard.useRuleExpired1')}</span>
                  </div>
                  <div className='basic-item'>
                    <span>{i18n.t('mkt.giftCard.payAtip')}</span>
                    <span className='info'>{giftCardInfo.giftcard.isPayTip ? i18n.t('mkt.common.standBy') : i18n.t('mkt.common.notSupport')}</span>
                  </div>
                  <div className='basic-item'>
                    <span>{i18n.t('mkt.giftCard.giftCardRecharge')}</span>
                    <span className='info'>{giftCardInfo.giftcard.allowAgainLoad ? i18n.t('mkt.common.standBy') : i18n.t('mkt.common.notSupport')}</span>
                  </div>
                </div>
              </div>
              <div className='detail-group'>
                <div className='group-top'>
                  <div className='title'>{i18n.t('mkt.member.applyStore')}</div>
                </div>
                <div className='group-body'>
                  <StoreShow list={handleStoreName(storeArr)}></StoreShow>
                </div>
              </div>
              <div className='detail-group'>
                <div className='group-top'>
                  <div className='title'>{i18n.t('mkt.member.useRule')}</div>
                </div>
                <div className='group-body'>
                  <div className='rule-item'>
                    {giftCardInfo.expireTime ? i18n.t('mkt.giftCard.useRuleExpired2', { text: giftCardInfo.expireTime }) : i18n.t('mkt.giftCard.useRuleExpired1')};
                    {giftCardInfo.giftcard.allowAgainLoad ? i18n.t('mkt.giftCard.useRuleRecharge1') : i18n.t('mkt.giftCard.useRuleRecharge2')};
                    {giftCardInfo.giftcard.isPayTip ? i18n.t('mkt.giftCard.useRulePayTip1') : i18n.t('mkt.giftCard.useRulePayTip2')}
                  </div>
                  <div className='rule-item'>{i18n.t('mkt.giftCard.useRuleExplain4')};</div>
                  <div className='rule-item'>{i18n.t('mkt.giftCard.useRuleExplain2')};</div>
                  <div className='rule-item'>{i18n.t('mkt.giftCard.useRuleExplain3')}.</div>
                </div>
              </div>
            </div>
        }
      </IonContent>
    </IonPage>
  )
}

export default observer(GiftCardDetail)
